import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface Kullanici {
    kullaniciID: number;
    adSoyad: string;
    email: string;
    telefon: string;
    kullaniciRolu: string;
    aktifMi: boolean;
}

interface ZamanKisitlamaSatir {
    gunAdi: string;
    baslangicSaati: string;
    bitisSaati: string;
}

interface ZamanKisitlamaKayit extends ZamanKisitlamaSatir {
    zamanKisitlamaID: number;
}

interface KullaniciForm {
    AdSoyad: string;
    Email: string;
    Sifre: string;
    Telefon: string;
    KullaniciRolu: string;
    AktifMi: boolean;
}

interface Proje {
    projeID: number;
    projeAdi: string;
}

const MODULLER = [
    { key: 'KullaniciYonetimi',  label: 'Kullanıcı Yönetimi' },
    { key: 'CariIslemleri',      label: 'Cari İşlemleri' },
    { key: 'MusteriBilgileri',   label: 'Müşteri Bilgileri' },
    { key: 'Projeler',           label: 'Projeler' },
    { key: 'Emlak',              label: 'Emlak' },
    { key: 'KiralikGayrimenkul', label: 'Kiralık Gayrimenkul' },
    { key: 'KatKarsiligArsa',    label: 'Kat Karşılığı Arsa' },
    { key: 'FinansalIslemler',   label: 'Finansal İşlemler' },
    { key: 'PotansiyelMusteri',  label: 'Potansiyel Müşteri' },
    { key: 'IsPlan',             label: 'İş Planı' },
    { key: 'Fihrist',            label: 'Fihrist' },
    { key: 'Ayarlar',            label: 'Ayarlar' },
];

// Derive initials from full name
const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Avatar background colors (deterministic by name)
const avatarColors = [
    { bg: '#dbeafe', color: '#1d4ed8' },
    { bg: '#ede9fe', color: '#6d28d9' },
    { bg: '#dcfce7', color: '#15803d' },
    { bg: '#fef3c7', color: '#b45309' },
    { bg: '#fee2e2', color: '#b91c1c' },
    { bg: '#e0f2fe', color: '#0369a1' },
    { bg: '#fce7f3', color: '#9d174d' },
    { bg: '#ffedd5', color: '#c2410c' },
];
const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
};

// Role badge styling
const roleMeta: Record<string, { label: string; bg: string; color: string }> = {
    Admin:     { label: 'Admin',     bg: '#fee2e2', color: '#b91c1c' },
    Yonetici:  { label: 'Yönetici',  bg: '#ede9fe', color: '#6d28d9' },
    Kullanici: { label: 'Kullanıcı', bg: '#dbeafe', color: '#1d4ed8' },
};
const getRoleMeta = (rol: string) =>
    roleMeta[rol] ?? { label: rol, bg: '#f1f5f9', color: '#475569' };

const KullaniciYonetimi: React.FC = () => {
    const { t } = useTranslation();
    const [kullanicilar, setKullanicilar] = useState<Kullanici[]>([]);
    const [selectedKullanici, setSelectedKullanici] = useState<Kullanici | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [formData, setFormData] = useState<KullaniciForm>({
        AdSoyad: '',
        Email: '',
        Sifre: '',
        Telefon: '',
        KullaniciRolu: 'Kullanici',
        AktifMi: true
    });

    const [showYetkiModal, setShowYetkiModal] = useState(false);
    const [yetkiKullanici, setYetkiKullanici] = useState<Kullanici | null>(null);
    const [yetkiForm, setYetkiForm] = useState<{ yetkiler: string[]; erisilebilirProjeler: number[] }>({ yetkiler: [], erisilebilirProjeler: [] });
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [yetkiLoading, setYetkiLoading] = useState(false);
    const [yetkiSaving, setYetkiSaving] = useState(false);

    const GUNLER = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

    const [showZamanModal, setShowZamanModal] = useState(false);
    const [zamanKullanici, setZamanKullanici] = useState<Kullanici | null>(null);
    const [zamanAktif, setZamanAktif] = useState(false);
    const [zamanKisitlamalar, setZamanKisitlamalar] = useState<ZamanKisitlamaKayit[]>([]);
    const [zamanLoading, setZamanLoading] = useState(false);
    const [zamanSaving, setZamanSaving] = useState(false);
    const [yeniSatir, setYeniSatir] = useState<ZamanKisitlamaSatir>({ gunAdi: 'Pazartesi', baslangicSaati: '09:00', bitisSaati: '18:00' });

    const fetchKullanicilar = async () => {
        try {
            const response = await axios.get('/api/Kullanicilar');
            setKullanicilar(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchProjeler = async () => {
        try {
            const res = await axios.get('/api/Projeler');
            setProjeler(res.data.map((p: any) => ({ projeID: p.projeID, projeAdi: p.projeAdi })));
        } catch {}
    };

    useEffect(() => {
        fetchKullanicilar();
        fetchProjeler();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (selectedKullanici) {
                await axios.put(`/api/Kullanicilar/${selectedKullanici.kullaniciID}`, formData);
                toast.success(t('msg.islemBasarili'));
            } else {
                await axios.post('/api/Kullanicilar/Kayit', formData);
                toast.success(t('msg.islemBasarili'));
            }
            fetchKullanicilar();
            handleCloseModal();
        } catch (error: any) {
            toast.error(error.response?.data?.message  || t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (kullanici: Kullanici) => {
        setSelectedKullanici(kullanici);
        setFormData({
            AdSoyad: kullanici.adSoyad,
            Email: kullanici.email,
            Sifre: '', // Şifre alanını boş bırakıyoruz
            Telefon: kullanici.telefon,
            KullaniciRolu: kullanici.kullaniciRolu,
            AktifMi: kullanici.aktifMi
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedKullanici(null);
        setFormData({
            AdSoyad: '',
            Email: '',
            Sifre: '',
            Telefon: '',
            KullaniciRolu: 'Kullanici',
            AktifMi: true
        });
    };

    const handleOpenYetkiModal = async (k: Kullanici) => {
        setYetkiKullanici(k);
        setYetkiLoading(true);
        setShowYetkiModal(true);
        try {
            const res = await axios.get(`/api/Kullanicilar/${k.kullaniciID}/yetkiler`);
            setYetkiForm({
                yetkiler: res.data.yetkiler ?? [],
                erisilebilirProjeler: res.data.erisilebilirProjeler ?? []
            });
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setYetkiLoading(false);
        }
    };

    const handleSaveYetkiler = async () => {
        if (!yetkiKullanici) return;
        setYetkiSaving(true);
        try {
            await axios.put(`/api/Kullanicilar/${yetkiKullanici.kullaniciID}/yetkiler`, yetkiForm);
            toast.success(t('msg.islemBasarili'));
            setShowYetkiModal(false);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setYetkiSaving(false);
        }
    };

    const toggleModul = (modul: string) => {
        setYetkiForm(prev => ({
            ...prev,
            yetkiler: prev.yetkiler.includes(modul)
                ? prev.yetkiler.filter(m => m !== modul)
                : [...prev.yetkiler, modul]
        }));
    };

    const toggleProje = (projeID: number) => {
        setYetkiForm(prev => ({
            ...prev,
            erisilebilirProjeler: prev.erisilebilirProjeler.includes(projeID)
                ? prev.erisilebilirProjeler.filter(id => id !== projeID)
                : [...prev.erisilebilirProjeler, projeID]
        }));
    };

    const handleOpenZamanModal = async (k: Kullanici) => {
        setZamanKullanici(k);
        setZamanLoading(true);
        setShowZamanModal(true);
        setYeniSatir({ gunAdi: 'Pazartesi', baslangicSaati: '09:00', bitisSaati: '18:00' });
        try {
            const res = await axios.get(`/api/Kullanicilar/${k.kullaniciID}/zaman-kisitlamalari`);
            setZamanAktif(res.data.aktif ?? false);
            setZamanKisitlamalar(res.data.kisitlamalar ?? []);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setZamanLoading(false);
        }
    };

    const handleSaveZaman = async () => {
        if (!zamanKullanici) return;
        setZamanSaving(true);
        try {
            await axios.put(`/api/Kullanicilar/${zamanKullanici.kullaniciID}/zaman-kisitlamalari`, {
                aktif: zamanAktif,
                kisitlamalar: zamanKisitlamalar.map(({ gunAdi, baslangicSaati, bitisSaati }) => ({ gunAdi, baslangicSaati, bitisSaati }))
            });
            toast.success(t('msg.islemBasarili'));
            setShowZamanModal(false);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setZamanSaving(false);
        }
    };

    const handleAddSatir = () => {
        if (!yeniSatir.gunAdi || !yeniSatir.baslangicSaati || !yeniSatir.bitisSaati) return;
        setZamanKisitlamalar(prev => [...prev, { ...yeniSatir, zamanKisitlamaID: Date.now() }]);
        setYeniSatir({ gunAdi: 'Pazartesi', baslangicSaati: '09:00', bitisSaati: '18:00' });
    };

    const handleRemoveSatir = (idx: number) => {
        setZamanKisitlamalar(prev => prev.filter((_, i) => i !== idx));
    };

    const filtered = kullanicilar.filter(k =>
        k.adSoyad.toLowerCase().includes(search.toLowerCase()) ||
        k.email.toLowerCase().includes(search.toLowerCase()) ||
        k.telefon?.includes(search)
    );

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                .ku-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #ffffff;
                }

                /* ── Header ── */
                .ku-header {
                    display: flex; align-items: center; justify-content: space-between;
                    margin-bottom: 24px;
                    flex-wrap: wrap; gap: 14px;
                }
                .ku-header-left { display: flex; flex-direction: column; gap: 3px; }
                .ku-title {
                    font-size: 20px; font-weight: 600; color: #111827;
                    letter-spacing: -0.4px; margin: 0;
                }
                .ku-subtitle { font-size: 13px; color: #6b7280; font-weight: 400; }
                .ku-header-right { display: flex; align-items: center; gap: 12px; }

                /* Search */
                .ku-search-wrap {
                    display: flex; align-items: center; gap: 9px;
                    background: #fff;
                    border: 1.5px solid #e2e8f0; border-radius: 10px;
                    padding: 8px 14px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.06);
                    transition: border-color 0.15s, box-shadow 0.15s;
                    min-width: 240px;
                }
                .ku-search-wrap:focus-within {
                    border-color: #cd6906;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.10);
                }
                .ku-search-wrap svg {
                    width: 16px; height: 16px; flex-shrink: 0;
                    stroke: #94a3b8; stroke-width: 2; fill: none;
                }
                .ku-search-input {
                    border: none; outline: none; background: transparent;
                    font-size: 13.5px; font-family: inherit; color: #1e293b;
                    width: 100%;
                }
                .ku-search-input::placeholder { color: #b0bec5; }

                /* Add button */
                .ku-add-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 9px 18px;
                    background: #cd6906; color: #fff;
                    border: none; border-radius: 10px;
                    font-size: 13.5px; font-weight: 600; font-family: inherit;
                    cursor: pointer; white-space: nowrap;
                    box-shadow: 0 4px 14px rgba(205,105,6,0.30);
                    transition: background 0.15s, box-shadow 0.15s, transform 0.12s;
                }
                .ku-add-btn:hover { background: #b85d05; transform: translateY(-1px); }
                .ku-add-btn svg { width: 15px; height: 15px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* Stats row */
                .ku-stats { display: flex; gap: 14px; margin-bottom: 22px; flex-wrap: wrap; }
                .ku-stat-card {
                    background: #fff; border-radius: 12px; padding: 16px 20px;
                    display: flex; align-items: center; gap: 14px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 2px 10px rgba(15,23,42,0.04);
                    flex: 1; min-width: 140px;
                }
                .ku-stat-icon {
                    width: 40px; height: 40px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .ku-stat-icon svg { width: 18px; height: 18px; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
                .ku-stat-val { font-size: 20px; font-weight: 600; color: #111827; line-height: 1; }
                .ku-stat-lbl { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 3px; }

                /* ── Card & Table ── */
                .ku-card {
                    background: #fff; border-radius: 14px;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                }
                .ku-table { width: 100%; border-collapse: collapse; }
                .ku-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .ku-table thead th {
                    padding: 13px 18px;
                    font-size: 11px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8; text-align: left; white-space: nowrap;
                }
                .ku-table thead th.ku-th-r { text-align: right; }
                .ku-table tbody tr { border-bottom: 1px solid #f8fafc; transition: background 0.12s; }
                .ku-table tbody tr:last-child { border-bottom: none; }
                .ku-table tbody tr.ku-row-hovered { background: #f0f5ff; }
                .ku-table tbody td { padding: 14px 18px; font-size: 13.5px; color: #1e293b; vertical-align: middle; }

                /* Avatar + identity */
                .ku-identity { display: flex; align-items: center; gap: 12px; }
                .ku-avatar {
                    width: 38px; height: 38px; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 13px; font-weight: 700; flex-shrink: 0;
                    letter-spacing: 0.5px;
                }
                .ku-name { font-weight: 600; color: #0f172a; font-size: 14px; line-height: 1.3; }
                .ku-email { font-size: 12px; color: #6b7280; font-weight: 400; margin-top: 2px; }

                /* Phone */
                .ku-phone-cell { display: flex; align-items: center; gap: 7px; color: #475569; }
                .ku-phone-cell svg { width: 13px; height: 13px; stroke: #94a3b8; stroke-width: 2; fill: none; flex-shrink: 0; }

                /* Role badge */
                .ku-role-badge {
                    display: inline-flex; align-items: center; gap: 5px;
                    padding: 4px 10px; border-radius: 20px;
                    font-size: 12px; font-weight: 600; letter-spacing: 0.02em;
                }

                /* Status pill */
                .ku-pill {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 4px 10px; border-radius: 20px;
                    font-size: 12px; font-weight: 600;
                }
                .ku-pill-dot { width: 6px; height: 6px; border-radius: 50%; }
                .ku-pill-aktif  { background: #dcfce7; color: #15803d; }
                .ku-pill-aktif  .ku-pill-dot { background: #22c55e; }
                .ku-pill-pasif  { background: #fee2e2; color: #b91c1c; }
                .ku-pill-pasif  .ku-pill-dot { background: #ef4444; }

                /* Ghost action buttons */
                .ku-actions { display: flex; justify-content: flex-end; gap: 6px; opacity: 0; transition: opacity 0.15s; }
                .ku-row-hovered .ku-actions { opacity: 1; }
                .ku-ghost-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 34px; height: 34px; border-radius: 8px;
                    border: 1.5px solid transparent; background: transparent;
                    cursor: pointer; transition: background 0.15s, border-color 0.15s; color: #64748b;
                }
                .ku-ghost-edit:hover   { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .ku-ghost-shield:hover { background: #f5f3ff; border-color: #ddd6fe; color: #7c3aed; }
                .ku-ghost-clock:hover  { background: #ecfdf5; border-color: #a7f3d0; color: #059669; }

                /* Zaman kısıtlama modal */
                .ku-zaman-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45);
                    backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1070; padding: 20px;
                }
                .ku-zaman-modal {
                    background: #fff; border-radius: 16px;
                    width: 100%; max-width: 560px; max-height: 85vh;
                    display: flex; flex-direction: column;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: kuModalIn 0.18s ease; overflow: hidden;
                }
                .ku-zaman-modal-body { padding: 20px 24px; overflow-y: auto; flex: 1; }
                .ku-zaman-toggle-row {
                    display: flex; align-items: center; gap: 10px;
                    padding: 12px 14px; border-radius: 10px;
                    background: #f8fafc; border: 1.5px solid #e2e8f0;
                    margin-bottom: 18px; cursor: pointer; user-select: none;
                }
                .ku-zaman-toggle-row.active { background: #ecfdf5; border-color: #6ee7b7; }
                .ku-zaman-toggle-lbl { font-size: 13.5px; font-weight: 600; color: #0f172a; }
                .ku-zaman-checkbox {
                    width: 18px; height: 18px; border-radius: 5px;
                    border: 2px solid #d1d5db; flex-shrink: 0;
                    display: flex; align-items: center; justify-content: center;
                    transition: background 0.15s, border-color 0.15s;
                }
                .ku-zaman-checkbox.checked { background: #059669; border-color: #059669; }
                .ku-zaman-table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
                .ku-zaman-table th {
                    font-size: 11px; font-weight: 700; letter-spacing: 0.5px;
                    color: #94a3b8; text-transform: uppercase;
                    padding: 8px 10px; text-align: left; border-bottom: 1.5px solid #f1f5f9;
                }
                .ku-zaman-table td { padding: 8px 10px; font-size: 13px; color: #334155; border-bottom: 1px solid #f8fafc; }
                .ku-zaman-table tr:last-child td { border-bottom: none; }
                .ku-zaman-del-btn {
                    width: 26px; height: 26px; border-radius: 6px; border: none;
                    background: #fee2e2; color: #b91c1c; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 13px; transition: background 0.15s;
                }
                .ku-zaman-del-btn:hover { background: #fecaca; }
                .ku-zaman-add-row {
                    display: flex; align-items: center; gap: 8px;
                    padding: 12px 14px; background: #f8fafc;
                    border-radius: 10px; border: 1.5px dashed #cbd5e1;
                }
                .ku-zaman-add-row select, .ku-zaman-add-row input[type="time"] {
                    padding: 7px 10px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 13px; font-family: inherit; color: #0f172a;
                    outline: none; background: #fff;
                    transition: border-color 0.15s;
                }
                .ku-zaman-add-row select:focus, .ku-zaman-add-row input[type="time"]:focus {
                    border-color: #059669;
                }
                .ku-zaman-add-btn {
                    padding: 7px 14px; border: none; border-radius: 8px;
                    background: #059669; color: #fff;
                    font-size: 12px; font-weight: 600; font-family: inherit;
                    cursor: pointer; white-space: nowrap;
                    transition: background 0.15s;
                }
                .ku-zaman-add-btn:hover { background: #047857; }
                .ku-zaman-empty { text-align: center; padding: 20px; color: #94a3b8; font-size: 13px; }
                .ku-zaman-gun-badge {
                    display: inline-block; padding: 2px 8px; border-radius: 5px;
                    background: #ede9fe; color: #6d28d9;
                    font-size: 12px; font-weight: 600;
                }

                /* Yetki modal */
                .ku-yetki-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45);
                    backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1060; padding: 20px;
                }
                .ku-yetki-modal {
                    background: #fff; border-radius: 16px;
                    width: 100%; max-width: 580px; max-height: 85vh;
                    display: flex; flex-direction: column;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: kuModalIn 0.18s ease; overflow: hidden;
                }
                .ku-yetki-modal-body { padding: 20px 24px; overflow-y: auto; flex: 1; }
                .ku-yetki-section-title {
                    font-size: 11px; font-weight: 700; letter-spacing: 0.7px;
                    color: #94a3b8; text-transform: uppercase; margin: 0 0 12px;
                }
                .ku-modul-grid {
                    display: grid; grid-template-columns: 1fr 1fr;
                    gap: 8px; margin-bottom: 20px;
                }
                .ku-modul-item {
                    display: flex; align-items: center; gap: 10px;
                    padding: 10px 12px; border-radius: 9px;
                    border: 1.5px solid #e2e8f0; cursor: pointer;
                    transition: border-color 0.15s, background 0.15s;
                    user-select: none;
                }
                .ku-modul-item:hover { border-color: #c4b5fd; background: #faf5ff; }
                .ku-modul-item.ku-modul-active { border-color: #7c3aed; background: #f5f3ff; }
                .ku-modul-check {
                    width: 18px; height: 18px; border-radius: 5px;
                    border: 2px solid #d1d5db; display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0; transition: background 0.15s, border-color 0.15s;
                }
                .ku-modul-active .ku-modul-check { background: #7c3aed; border-color: #7c3aed; }
                .ku-modul-label { font-size: 13px; font-weight: 500; color: #334155; }
                .ku-proje-grid { display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px; }
                .ku-proje-item {
                    display: flex; align-items: center; gap: 10px;
                    padding: 9px 12px; border-radius: 9px;
                    border: 1.5px solid #e2e8f0; cursor: pointer;
                    transition: border-color 0.15s, background 0.15s;
                    user-select: none;
                }
                .ku-proje-item:hover { border-color: #93c5fd; background: #eff6ff; }
                .ku-proje-item.ku-proje-active { border-color: #3b82f6; background: #eff6ff; }
                .ku-proje-active .ku-modul-check { background: #3b82f6; border-color: #3b82f6; }
                .ku-admin-notice {
                    background: #fef3c7; border: 1.5px solid #fcd34d; border-radius: 10px;
                    padding: 12px 16px; font-size: 13px; color: #92400e; margin-bottom: 16px;
                    display: flex; align-items: center; gap: 8px;
                }
                .ku-yetki-divider { border: none; border-top: 1.5px solid #f1f5f9; margin: 16px 0; }
                .ku-yetki-loading { text-align: center; padding: 40px; color: #94a3b8; font-size: 14px; }
                .ku-ghost-btn svg { width: 15px; height: 15px; stroke: currentColor; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; }

                /* Footer */
                .ku-footer {
                    padding: 13px 18px; border-top: 1.5px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .ku-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .ku-count span { color: #475569; font-weight: 600; }

                /* Empty */
                .ku-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .ku-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .ku-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .ku-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45);
                    backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1050; padding: 20px;
                }
                .ku-modal {
                    background: #fff; border-radius: 16px;
                    width: 100%; max-width: 480px;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: kuModalIn 0.18s ease; overflow: hidden;
                }
                @keyframes kuModalIn {
                    from { opacity: 0; transform: translateY(-12px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .ku-modal-header {
                    padding: 22px 24px 18px; border-bottom: 1px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .ku-modal-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; }
                .ku-modal-close {
                    width: 30px; height: 30px; border-radius: 8px; border: none;
                    background: #f1f5f9; color: #64748b; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .ku-modal-close:hover { background: #e2e8f0; color: #0f172a; }
                .ku-modal-body   { padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
                .ku-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .ku-field label {
                    display: block; font-size: 11.5px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b; margin-bottom: 6px;
                }
                .ku-field input, .ku-field select {
                    width: 100%; padding: 10px 13px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 13.5px; font-family: inherit; color: #0f172a;
                    outline: none; box-sizing: border-box;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    background: #fff;
                }
                .ku-field input:focus, .ku-field select:focus {
                    border-color: #111827;
                    box-shadow: 0 0 0 3px rgba(17,24,39,0.08);
                }
                .ku-field-hint { font-size: 11px; color: #94a3b8; margin-top: 4px; }

                /* Toggle switch */
                .ku-toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 6px 0; }
                .ku-toggle-lbl { font-size: 13.5px; font-weight: 500; color: #334155; }
                .ku-switch { position: relative; width: 42px; height: 24px; display: inline-block; }
                .ku-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
                .ku-track {
                    position: absolute; inset: 0; border-radius: 12px;
                    background: #e2e8f0; cursor: pointer;
                    transition: background 0.2s;
                }
                .ku-track::before {
                    content: ''; position: absolute;
                    width: 18px; height: 18px; border-radius: 50%;
                    background: #fff; left: 3px; top: 3px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
                    transition: transform 0.2s;
                }
                .ku-switch input:checked + .ku-track { background: #22c55e; }
                .ku-switch input:checked + .ku-track::before { transform: translateX(18px); }

                .ku-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .ku-btn-cancel {
                    padding: 9px 18px; border-radius: 9px; border: 1.5px solid #e2e8f0;
                    background: #fff; color: #64748b;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .ku-btn-cancel:hover { background: #f1f5f9; }
                .ku-btn-save {
                    padding: 9px 22px; border-radius: 9px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 2px 8px rgba(205,105,6,0.28);
                    transition: background 0.15s, transform 0.1s;
                }
                .ku-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .ku-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .ku-spinner {
                    width: 13px; height: 13px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: kuSpin 0.7s linear infinite;
                }
                @keyframes kuSpin { to { transform: rotate(360deg); } }
            `}</style>

            <div className="ku-page">
                {/* Header */}
                <div className="ku-header">
                    <div className="ku-header-left">
                        <h1 className="ku-title">{t('kullanici.yonetim')}</h1>
                        <span className="ku-subtitle">Yönetim &rsaquo; Kullanıcılar</span>
                    </div>
                    <div className="ku-header-right">
                        <div className="ku-search-wrap">
                            <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            <input
                                className="ku-search-input"
                                type="text"
                                placeholder="Ad, e-posta veya telefon ara..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <button className="ku-add-btn" onClick={() => setShowModal(true)}>
                            <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Yeni Kullanıcı Ekle
                        </button>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="ku-stats">
                    <div className="ku-stat-card">
                        <div className="ku-stat-icon" style={{ background: '#fff8f0' }}>
                            <svg viewBox="0 0 24 24" stroke="#cd6906"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        </div>
                        <div>
                            <div className="ku-stat-val">{kullanicilar.length}</div>
                            <div className="ku-stat-lbl">Toplam Kullanıcı</div>
                        </div>
                    </div>
                    <div className="ku-stat-card">
                        <div className="ku-stat-icon" style={{ background: '#dcfce7' }}>
                            <svg viewBox="0 0 24 24" stroke="#15803d"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        </div>
                        <div>
                            <div className="ku-stat-val">{kullanicilar.filter(k => k.aktifMi).length}</div>
                            <div className="ku-stat-lbl">Aktif Kullanıcı</div>
                        </div>
                    </div>
                    <div className="ku-stat-card">
                        <div className="ku-stat-icon" style={{ background: '#ede9fe' }}>
                            <svg viewBox="0 0 24 24" stroke="#6d28d9"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        </div>
                        <div>
                            <div className="ku-stat-val">{kullanicilar.filter(k => k.kullaniciRolu === 'Admin' || k.kullaniciRolu === 'Yonetici').length}</div>
                            <div className="ku-stat-lbl">{t('kullanici.yonetici')}</div>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="ku-card">
                    <table className="ku-table">
                        <thead>
                            <tr>
                                <th>{t('label.kullanici')}</th>
                                <th>{t('label.telefon')}</th>
                                <th>Kullanıcı Tipi</th>
                                <th>{t('label.durum')}</th>
                                <th className="ku-th-r">{t('label.islem')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={5}>
                                        <div className="ku-empty">
                                            <div className="ku-empty-icon">👤</div>
                                            <div className="ku-empty-text">
                                                {search ? 'Aramanıza uygun kullanıcı bulunamadı' : 'Henüz kullanıcı eklenmedi'}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map(k => {
                                    const initials = getInitials(k.adSoyad);
                                    const avColor = getAvatarColor(k.adSoyad);
                                    const role = getRoleMeta(k.kullaniciRolu);
                                    return (
                                        <tr
                                            key={k.kullaniciID}
                                            className={hoveredRow === k.kullaniciID ? 'ku-row-hovered' : ''}
                                            onMouseEnter={() => setHoveredRow(k.kullaniciID)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                        >
                                            <td>
                                                <div className="ku-identity">
                                                    <div className="ku-avatar" style={{ background: avColor.bg, color: avColor.color }}>
                                                        {initials}
                                                    </div>
                                                    <div>
                                                        <div className="ku-name">{k.adSoyad}</div>
                                                        <div className="ku-email">{k.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="ku-phone-cell">
                                                    <svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 11.39 19a19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 3.09 4.18 2 2 0 0 1 5.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                                    {k.telefon || '—'}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="ku-role-badge" style={{ background: role.bg, color: role.color }}>
                                                    {role.label}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`ku-pill ${k.aktifMi ? 'ku-pill-aktif' : 'ku-pill-pasif'}`}>
                                                    <span className="ku-pill-dot" />
                                                    {k.aktifMi ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="ku-actions">
                                                    <button
                                                        className="ku-ghost-btn ku-ghost-edit"
                                                        title={t('btn.duzenle')}
                                                        onClick={() => handleEdit(k)}
                                                    >
                                                        <svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                    </button>
                                                    <button
                                                        className="ku-ghost-btn ku-ghost-shield"
                                                        title="Yetkiler"
                                                        onClick={() => handleOpenYetkiModal(k)}
                                                    >
                                                        <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                                    </button>
                                                    {k.kullaniciRolu !== 'Admin' && (
                                                        <button
                                                            className="ku-ghost-btn ku-ghost-clock"
                                                            title="Giriş Zaman Kısıtlaması"
                                                            onClick={() => handleOpenZamanModal(k)}
                                                        >
                                                            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {kullanicilar.length > 0 && (
                        <div className="ku-footer">
                            <span className="ku-count">
                                <span>{filtered.length}</span> / {kullanicilar.length} kullanıcı gösteriliyor
                            </span>
                            <span className="ku-count">
                                <span>{kullanicilar.filter(k => k.aktifMi).length}</span> aktif
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="ku-overlay" onClick={handleCloseModal}>
                    <div className="ku-modal" onClick={e => e.stopPropagation()}>
                        <div className="ku-modal-header">
                            <h2 className="ku-modal-title">
                                {selectedKullanici ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
                            </h2>
                            <button className="ku-modal-close" onClick={handleCloseModal} aria-label={t('btn.kapat')}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="ku-modal-body">
                                <div className="ku-form-row">
                                    <div className="ku-field">
                                        <label>{t('label.adSoyad')}</label>
                                        <input
                                            type="text"
                                            placeholder={t('label.ara')}
                                            value={formData.AdSoyad}
                                            onChange={e => setFormData({ ...formData, AdSoyad: e.target.value })}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                    <div className="ku-field">
                                        <label>Telefon</label>
                                        <input
                                            type="tel"
                                            placeholder="05XX XXX XX XX"
                                            value={formData.Telefon}
                                            onChange={e => setFormData({ ...formData, Telefon: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="ku-field">
                                    <label>{t('label.eposta')}</label>
                                    <input
                                        type="email"
                                        placeholder="kullanici@sirket.com"
                                        value={formData.Email}
                                        onChange={e => setFormData({ ...formData, Email: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="ku-form-row">
                                    <div className="ku-field">
                                        <label>{selectedKullanici ? 'Yeni Şifre' : 'Şifre'}</label>
                                        <input
                                            type="password"
                                            placeholder={selectedKullanici ? 'Boş bırakılırsa değişmez' : '••••••••'}
                                            value={formData.Sifre}
                                            onChange={e => setFormData({ ...formData, Sifre: e.target.value })}
                                            required={!selectedKullanici}
                                        />
                                        {selectedKullanici && <div className="ku-field-hint">Boş bırakırsanız şifre güncellenmez.</div>}
                                    </div>
                                    <div className="ku-field">
                                        <label>Kullanıcı Tipi</label>
                                        <select
                                            value={formData.KullaniciRolu}
                                            onChange={e => setFormData({ ...formData, KullaniciRolu: e.target.value })}
                                        >
                                            <option value="Kullanici">{t('label.kullanici')}</option>
                                            <option value="Yonetici">{t('kullanici.yonetici')}</option>
                                            <option value="Admin">Admin</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="ku-toggle-row">
                                    <span className="ku-toggle-lbl">Hesap Durumu — Aktif</span>
                                    <label className="ku-switch">
                                        <input
                                            type="checkbox"
                                            checked={formData.AktifMi}
                                            onChange={e => setFormData({ ...formData, AktifMi: e.target.checked })}
                                        />
                                        <span className="ku-track" />
                                    </label>
                                </div>
                            </div>
                            <div className="ku-modal-footer">
                                <button type="button" className="ku-btn-cancel" onClick={handleCloseModal}>{t('btn.iptal')}</button>
                                <button type="submit" className="ku-btn-save" disabled={saving}>
                                    {saving && <span className="ku-spinner" />}
                                    {selectedKullanici ? t('btn.kaydet') : t('btn.ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Yetki Modalı */}
            {showYetkiModal && yetkiKullanici && (
                <div className="ku-yetki-overlay" onClick={() => setShowYetkiModal(false)}>
                    <div className="ku-yetki-modal" onClick={e => e.stopPropagation()}>
                        <div className="ku-modal-header">
                            <h2 className="ku-modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="fas fa-shield-alt" style={{ color: '#7c3aed' }}></i>
                                {yetkiKullanici.adSoyad} — Yetkiler
                            </h2>
                            <button className="ku-modal-close" onClick={() => setShowYetkiModal(false)} aria-label={t('btn.kapat')}>✕</button>
                        </div>
                        <div className="ku-yetki-modal-body">
                            {yetkiLoading ? (
                                <div className="ku-yetki-loading">Yetkiler yükleniyor...</div>
                            ) : (
                                <>
                                    {yetkiKullanici.kullaniciRolu === 'Admin' && (
                                        <div className="ku-admin-notice">
                                            <i className="fas fa-info-circle"></i>
                                            Admin rolündeki kullanıcıların tüm modüllere otomatik erişimi vardır.
                                        </div>
                                    )}
                                    <p className="ku-yetki-section-title">Modül Yetkileri</p>
                                    <div className="ku-modul-grid">
                                        {MODULLER.map(m => {
                                            const isAdmin = yetkiKullanici.kullaniciRolu === 'Admin';
                                            const checked = isAdmin || yetkiForm.yetkiler.includes(m.key);
                                            return (
                                                <div
                                                    key={m.key}
                                                    className={`ku-modul-item ${checked ? 'ku-modul-active' : ''}`}
                                                    onClick={() => !isAdmin && toggleModul(m.key)}
                                                    style={isAdmin ? { opacity: 0.65, cursor: 'default' } : {}}
                                                >
                                                    <div className="ku-modul-check">
                                                        {checked && <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                    </div>
                                                    <span className="ku-modul-label">{m.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {yetkiForm.yetkiler.includes('Projeler') && projeler.length > 0 && (
                                        <>
                                            <hr className="ku-yetki-divider" />
                                            <p className="ku-yetki-section-title">Proje Erişimi</p>
                                            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                                                Boş bırakılırsa tüm projelere erişim verilir.
                                            </p>
                                            <div className="ku-proje-grid">
                                                {projeler.map(p => {
                                                    const active = yetkiForm.erisilebilirProjeler.includes(p.projeID);
                                                    return (
                                                        <div
                                                            key={p.projeID}
                                                            className={`ku-proje-item ${active ? 'ku-proje-active' : ''}`}
                                                            onClick={() => toggleProje(p.projeID)}
                                                        >
                                                            <div className="ku-modul-check">
                                                                {active && <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                            </div>
                                                            <span className="ku-modul-label">{p.projeAdi}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="ku-modal-footer">
                            <button type="button" className="ku-btn-cancel" onClick={() => setShowYetkiModal(false)}>{t('btn.iptal')}</button>
                            <button
                                type="button"
                                className="ku-btn-save"
                                style={{ background: '#7c3aed', boxShadow: '0 2px 8px rgba(124,58,237,0.28)' }}
                                disabled={yetkiSaving || yetkiKullanici.kullaniciRolu === 'Admin'}
                                onClick={handleSaveYetkiler}
                            >
                                {yetkiSaving && <span className="ku-spinner" />}
                                Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Zaman Kısıtlaması Modalı */}
            {showZamanModal && zamanKullanici && (
                <div className="ku-zaman-overlay" onClick={() => setShowZamanModal(false)}>
                    <div className="ku-zaman-modal" onClick={e => e.stopPropagation()}>
                        <div className="ku-modal-header">
                            <h2 className="ku-modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="fas fa-clock" style={{ color: '#059669' }}></i>
                                {zamanKullanici.adSoyad} — Giriş Zaman Kısıtlaması
                            </h2>
                            <button className="ku-modal-close" onClick={() => setShowZamanModal(false)} aria-label={t('btn.kapat')}>✕</button>
                        </div>
                        <div className="ku-zaman-modal-body">
                            {zamanLoading ? (
                                <div className="ku-yetki-loading">{t('msg.yukleniyor')}</div>
                            ) : (
                                <>
                                    {/* Aktif Checkbox */}
                                    <div
                                        className={`ku-zaman-toggle-row${zamanAktif ? ' active' : ''}`}
                                        onClick={() => setZamanAktif(v => !v)}
                                    >
                                        <div className={`ku-zaman-checkbox${zamanAktif ? ' checked' : ''}`}>
                                            {zamanAktif && (
                                                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                                    <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </div>
                                        <span className="ku-zaman-toggle-lbl">Zaman Kısıtlaması Var</span>
                                    </div>

                                    {zamanAktif && (
                                        <>
                                            {/* Mevcut satırlar */}
                                            {zamanKisitlamalar.length > 0 ? (
                                                <table className="ku-zaman-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Gün</th>
                                                            <th>Başlangıç</th>
                                                            <th>Bitiş</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {zamanKisitlamalar.map((satir, idx) => (
                                                            <tr key={idx}>
                                                                <td><span className="ku-zaman-gun-badge">{satir.gunAdi}</span></td>
                                                                <td>{satir.baslangicSaati}</td>
                                                                <td>{satir.bitisSaati}</td>
                                                                <td>
                                                                    <button
                                                                        className="ku-zaman-del-btn"
                                                                        onClick={() => handleRemoveSatir(idx)}
                                                                        title="Kaldır"
                                                                    >
                                                                        <i className="fas fa-times"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <div className="ku-zaman-empty">Henüz zaman aralığı eklenmedi.</div>
                                            )}

                                            {/* Yeni satır ekleme formu */}
                                            <div className="ku-zaman-add-row">
                                                <select
                                                    value={yeniSatir.gunAdi}
                                                    onChange={e => setYeniSatir(v => ({ ...v, gunAdi: e.target.value }))}
                                                >
                                                    {GUNLER.map(g => <option key={g} value={g}>{g}</option>)}
                                                </select>
                                                <input
                                                    type="time"
                                                    value={yeniSatir.baslangicSaati}
                                                    onChange={e => setYeniSatir(v => ({ ...v, baslangicSaati: e.target.value }))}
                                                />
                                                <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                                                <input
                                                    type="time"
                                                    value={yeniSatir.bitisSaati}
                                                    onChange={e => setYeniSatir(v => ({ ...v, bitisSaati: e.target.value }))}
                                                />
                                                <button className="ku-zaman-add-btn" onClick={handleAddSatir}>
                                                    <i className="fas fa-plus"></i> Ekle
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="ku-modal-footer">
                            <button type="button" className="ku-btn-cancel" onClick={() => setShowZamanModal(false)}>{t('btn.iptal')}</button>
                            <button
                                type="button"
                                className="ku-btn-save"
                                style={{ background: '#059669', boxShadow: '0 2px 8px rgba(5,150,105,0.28)' }}
                                disabled={zamanSaving}
                                onClick={handleSaveZaman}
                            >
                                {zamanSaving && <span className="ku-spinner" />}
                                Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default KullaniciYonetimi;